<template>
  <div ref="carousel" class="carousel-container">
    <button
      v-if="visibleControls$"
      :disabled="disabledBack$"
      @click="changePage('back')"
      class="btn back"
    >
      <i class="material-icons">
        keyboard_arrow_left
      </i>
    </button>
    <transition-group
      name="recommendation-transition"
      tag="div"
      class="recommendation-card-container"
    >
      <recommendationCard
        v-for="recommendation in recommendations$"
        :recommendation="recommendation"
        :key="recommendation.id"
        v-stream:loadCard="loadCard$"
        @recommendationButtonClicked="recommendationButtonClicked"
      />
    </transition-group>
    <button
      v-if="visibleControls$"
      :disabled="disabledNext$"
      @click="changePage('next')"
      class="btn next"
    >
      <i class="material-icons">
        keyboard_arrow_right
      </i>
    </button>
    <div class="page-row" v-if="pages$ && pages$.length > 1">
      <span
        v-for="page in pages$"
        :key="page"
        class="clickable"
        :class="{ active: page + 1 === currentPage }"
        @click="goToPage(page + 1)"
        >&nbsp;</span
      >
    </div>
  </div>
</template>

<script>
import { Subject, merge, combineLatest, fromEvent } from 'rxjs'
import { map, pluck, mapTo, startWith, take } from 'rxjs/operators'
import RecommendationCard from '@/components/RecommendationCard.vue'

export default {
  components: {
    RecommendationCard,
  },
  props: {
    recommendations: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      currentPage: 1,
    }
  },
  subscriptions() {
    // listen event from recommendationCard component
    this.loadCard$ = new Subject()

    // <Observable> recommendations from props
    const recommendationsSource$ = this.$watchAsObservable(
      'recommendations'
    ).pipe(
      pluck('newValue'),
      startWith(this.recommendations)
    )

    // <Observable> current page
    const currentPage$ = this.$watchAsObservable('currentPage').pipe(
      pluck('newValue'),
      startWith(this.currentPage)
    )

    // <Observable> card's width
    const getCardWidth$ = this.loadCard$.pipe(
      pluck('event', 'msg', 'offsetWidth'),
      take(1)
    )

    // <Observable> mounted version
    const mounted$ = this.$eventToObservable('hook:mounted')

    // <Observable> event for window resize
    const resize$ = fromEvent(window, 'resize')

    // <Observable> carousel's width
    const getCarouselWidth$ = merge(mounted$, resize$).pipe(
      mapTo(this.$refs),
      pluck('carousel', 'offsetWidth')
    )

    // <Observable> number of visible cards
    const maxCards$ = combineLatest(getCardWidth$, getCarouselWidth$).pipe(
      map(this.divideValues)
    )

    // <Observable> recommendations that should be show
    const recommendationsToShow$ = combineLatest(
      recommendationsSource$,
      maxCards$,
      currentPage$
    ).pipe(
      map(([recommendations, maxCards, currentPage]) =>
        recommendations.filter(
          this.toggleRecommendationShow(
            ...this.indexToShow(currentPage, maxCards)
          )
        )
      )
    )

    // <Observable> only first recommendation from API used just to first render
    const recommendationFirstRender$ = recommendationsSource$.pipe(
      map(recommendations =>
        recommendations.filter((recommendation, index) => index === 0)
      )
    )

    // <Observable> show 'recommendationFirstRender' while 'recommendationsToShow' is loading
    const recommendations$ = merge(
      recommendationFirstRender$,
      recommendationsToShow$
    )

    // <Observable> disable back button when currentPage is value 1
    const disabledBack$ = currentPage$.pipe(map(page => page === 1))

    // <Observable> disable next button when currentPage is at last page
    const disabledNext$ = combineLatest(
      currentPage$,
      maxCards$,
      recommendationsSource$.pipe(
        map(recommendations => recommendations.length)
      )
    ).pipe(
      map(
        ([currentPage, maxCards, recommendationsLength]) =>
          currentPage === Math.ceil(recommendationsLength / maxCards)
      )
    )

    // <Observable> show or hide buttons back and next
    const visibleControls$ = combineLatest(
      recommendationsSource$,
      maxCards$
    ).pipe(
      map(([recommendation, maxCards]) => recommendation.length > maxCards),
      startWith(false)
    )

    const pages$ = combineLatest(maxCards$, recommendationsSource$).pipe(
      map(([maxCards, recommendations]) => {
        const length =
          maxCards && recommendations.length
            ? Math.ceil(recommendations.length / maxCards)
            : 0
        return [...Array(length).keys()]
      })
    )

    return {
      recommendations$,
      disabledBack$,
      disabledNext$,
      visibleControls$,
      pages$,
    }
  },
  methods: {
    recommendationButtonClicked(recommendation) {
      this.$emit('recommendationButtonClicked', recommendation)
    },
    divideValues([cardOffsetWidth, carouselOffsetWidth]) {
      return parseInt(parseInt(carouselOffsetWidth) / parseInt(cardOffsetWidth))
    },
    toggleRecommendationShow(currentPage, maxCards) {
      return (recommendation, index) => index >= currentPage && index < maxCards
    },
    indexToShow(currentPage, maxCards) {
      return [currentPage * maxCards - maxCards, currentPage * maxCards]
    },
    changePage(action) {
      action === 'back' ? this.currentPage-- : this.currentPage++
    },
    goToPage(pageNumber) {
      this.currentPage = pageNumber
    },
  },
}
</script>
<style lang="scss" scoped>
@import '~@/styles/custom/colors';
@import '~bootstrap/scss/_functions';
@import '~bootstrap/scss/_variables';
@import '~bootstrap/scss/mixins/breakpoints';

.carousel-container {
  position: relative;
}

.recommendation-transition-active,
.recommendation-transition-active {
  transition: all 1s;
}
.recommendation-transition,
.recommendation-transition-to {
  opacity: 0;
}

.recommendation-card-container {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  overflow: hidden;

  @include media-breakpoint-down(sm) {
    justify-content: center;
  }
}

.btn.back,
.btn.next {
  align-items: center;
  background-color: map-get($theme-colors, 'white');
  border: 1px solid map-get($theme-colors, 'gray01') !important;
  border-radius: 30px;
  color: map-get($theme-colors, 'black');
  display: flex;
  font-size: 16px;
  height: 34px;
  justify-content: center;
  margin: 0 !important;
  position: absolute;
  text-rendering: auto;
  transition: 100ms ease all;
  width: 48px;

  &:not(:disabled) i {
    cursor: pointer;
  }
}

.btn.next {
  height: 48px;
  top: 190px;
  right: -26px;

  @media (max-width: '1023px') {
    right: 5px;
  }

  @media (max-width: '425px') {
    width: 48px;
    height: 48px !important;
  }

  &:disabled {
    display: none;
  }
}

.btn.back {
  height: 48px;
  left: -26px;
  top: 190px;

  @media (max-width: '1023px') {
    left: 5px;
    width: 48px;
  }

  @media (max-width: '425px') {
    width: 48px;
    height: 48px !important;
  }

  &:disabled {
    display: none;
  }
}

.page-row {
  padding-top: 10px;
  text-align: center;

  span {
    &:before {
      content: '•';
      color: map-get($theme-colors, 'purple05');
      font-size: 25px;
    }

    &:hover:before,
    &.active:before {
      color: map-get($theme-colors, 'primary');
    }
  }
}
</style>
